/**
 * Double-click cursor demo.
 *
 * Renders, as a screen-fixed portal, a virtual mouse cursor that drifts
 * onto a target node and pulses out two quick click rings — the visual
 * vocabulary of a double-click. Used on steps where the user is meant
 * to double-click a specific canvas node to open it (e.g. the deck on
 * DeckWorkingStep).
 *
 * The target's bounding rect is re-measured every frame so the demo
 * tracks canvas pan/zoom. Honors `prefers-reduced-motion`.
 */

import { memo, useEffect, useState } from "react";
import { createPortal } from "react-dom";

function cssEscape(s: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(s);
  }
  return s.replace(/[^a-zA-Z0-9_-]/g, (c) => `\\${c}`);
}

const STYLES = `
  /* Cursor: enters from upper-right of the node, glides onto the
     center (translate 0,0 = tip on center), pulses twice (the two
     click moments at 32% and 44%), then fades and resets. */
  @keyframes _co_onb_dblclick_cursor {
    0%   { transform: translate(56px, -48px) scale(1);    opacity: 0; }
    8%   { transform: translate(56px, -48px) scale(1);    opacity: 1; }
    28%  { transform: translate(0px,  0px)  scale(1);     opacity: 1; }
    32%  { transform: translate(0px,  0px)  scale(0.84);  opacity: 1; }
    38%  { transform: translate(0px,  0px)  scale(1);     opacity: 1; }
    44%  { transform: translate(0px,  0px)  scale(0.84);  opacity: 1; }
    50%  { transform: translate(0px,  0px)  scale(1);     opacity: 1; }
    78%  { transform: translate(0px,  0px)  scale(1);     opacity: 1; }
    90%  { transform: translate(0px,  0px)  scale(1);     opacity: 0; }
    100% { transform: translate(56px, -48px) scale(1);    opacity: 0; }
  }
  ._co_onb_dblclick_cursor {
    position: fixed;
    width: 42px;
    height: 42px;
    pointer-events: none;
    /* Theme-aware: dark cursor on light canvas, light cursor on dark
       canvas, with opposite-tone stroke so the outline always reads. */
    color: #1A1A16;
    --co-cursor-stroke: #F3EFE6;
    filter: drop-shadow(0 2px 4px rgba(0,0,0,0.22));
    z-index: 52;
    animation: _co_onb_dblclick_cursor 3.4s cubic-bezier(0.45, 0, 0.2, 1) infinite;
  }
  .dark ._co_onb_dblclick_cursor {
    color: #F3EFE6;
    --co-cursor-stroke: #1A1A16;
  }

  /* Two click rings, phased to the two cursor scale pulses. */
  @keyframes _co_onb_dblclick_ring_1 {
    0%, 30%  { transform: translate(-50%, -50%) scale(0.4); opacity: 0; }
    34%      { transform: translate(-50%, -50%) scale(0.4); opacity: 0.75; }
    46%      { transform: translate(-50%, -50%) scale(1.6); opacity: 0; }
    100%     { transform: translate(-50%, -50%) scale(1.6); opacity: 0; }
  }
  @keyframes _co_onb_dblclick_ring_2 {
    0%, 42%  { transform: translate(-50%, -50%) scale(0.4); opacity: 0; }
    46%      { transform: translate(-50%, -50%) scale(0.4); opacity: 0.75; }
    58%      { transform: translate(-50%, -50%) scale(1.6); opacity: 0; }
    100%     { transform: translate(-50%, -50%) scale(1.6); opacity: 0; }
  }
  ._co_onb_dblclick_ring {
    position: fixed;
    width: 56px;
    height: 56px;
    border-radius: 999px;
    border: 2px solid #B86E49;
    pointer-events: none;
    opacity: 0;
    z-index: 51;
  }
  ._co_onb_dblclick_ring_1 {
    animation: _co_onb_dblclick_ring_1 3.4s cubic-bezier(0.2, 0, 0, 1) infinite;
  }
  ._co_onb_dblclick_ring_2 {
    animation: _co_onb_dblclick_ring_2 3.4s cubic-bezier(0.2, 0, 0, 1) infinite;
  }

  /* Subtle breathing glow on the node itself so even when the cursor
     fades out between cycles the user still sees "this one". Scoped
     by data-id to avoid bleeding onto unrelated nodes. Applied as an
     inline <style> built with the escaped node id. */
  @keyframes _co_onb_dblclick_node_glow {
    0%, 100% {
      filter: drop-shadow(0 0 0 rgba(184, 110, 73, 0.0))
              drop-shadow(0 8px 24px rgba(184, 110, 73, 0.18));
    }
    50% {
      filter: drop-shadow(0 0 6px rgba(184, 110, 73, 0.45))
              drop-shadow(0 12px 36px rgba(184, 110, 73, 0.32));
    }
  }

  /* Slight rust outline around the node, breathing in lockstep with
     the glow. Uses outline (not border) so layout isn't pushed and
     the existing node border stays intact. */
  @keyframes _co_onb_dblclick_node_outline {
    0%, 100% { outline-color: rgba(184, 110, 73, 0.45); }
    50%      { outline-color: rgba(184, 110, 73, 0.85); }
  }

  @media (prefers-reduced-motion: reduce) {
    ._co_onb_dblclick_cursor,
    ._co_onb_dblclick_ring_1,
    ._co_onb_dblclick_ring_2 { animation: none !important; }
  }
`;

function DoubleClickCursorGlyph() {
  return (
    <svg
      width="42"
      height="42"
      viewBox="0 0 28 28"
      fill="none"
      style={{ display: "block" }}
      aria-hidden
    >
      <path
        d="M5 3 L5 22 L10.5 17.5 L13.5 24.5 L16.5 23.2 L13.5 16.2 L21 16.2 Z"
        fill="currentColor"
        stroke="var(--co-cursor-stroke, #ffffff)"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export const DoubleClickDemo = memo(function DoubleClickDemo({
  nodeId,
}: {
  nodeId: string;
}) {
  const [center, setCenter] = useState<{ x: number; y: number } | null>(null);
  const escapedId = cssEscape(nodeId);

  useEffect(() => {
    const selector = `.react-flow__node[data-id="${escapedId}"]`;
    let raf = 0;
    let last = "";
    const loop = () => {
      const el = document.querySelector<HTMLElement>(selector);
      if (el) {
        const r = el.getBoundingClientRect();
        const next = { x: r.left + r.width / 2, y: r.top + r.height / 2 };
        const serialized = `${next.x}|${next.y}`;
        if (serialized !== last) {
          last = serialized;
          setCenter(next);
        }
      } else if (last !== "") {
        last = "";
        setCenter(null);
      }
      raf = window.requestAnimationFrame(loop);
    };
    raf = window.requestAnimationFrame(loop);
    return () => window.cancelAnimationFrame(raf);
  }, [escapedId]);

  // Node-scoped glow CSS — only emitted when we have a target, so the
  // selector is anchored to the escaped id and won't accidentally
  // glow another node mid-frame.
  const nodeGlowCss = `
    .react-flow__node[data-id="${escapedId}"] {
      outline: 2px solid rgba(184, 110, 73, 0.55);
      outline-offset: 4px;
      border-radius: inherit;
      animation:
        _co_onb_dblclick_node_glow 2.4s ease-in-out infinite,
        _co_onb_dblclick_node_outline 2.4s ease-in-out infinite;
    }
    @media (prefers-reduced-motion: reduce) {
      .react-flow__node[data-id="${escapedId}"] { animation: none !important; }
    }
  `;

  return createPortal(
    <>
      <style>{STYLES}</style>
      <style>{nodeGlowCss}</style>
      {center && (
        <>
          <span
            aria-hidden
            className="_co_onb_dblclick_ring _co_onb_dblclick_ring_1"
            style={{ left: center.x, top: center.y }}
          />
          <span
            aria-hidden
            className="_co_onb_dblclick_ring _co_onb_dblclick_ring_2"
            style={{ left: center.x, top: center.y }}
          />
          {/* Cursor tip is at SVG (5, 3); offset so the tip sits on
              the node center when the keyframe translate is (0, 0). */}
          <span
            aria-hidden
            className="_co_onb_dblclick_cursor"
            style={{ left: center.x - 5, top: center.y - 3 }}
          >
            <DoubleClickCursorGlyph />
          </span>
        </>
      )}
    </>,
    document.body,
  );
});
